import { type SessionSummaryQuery } from "@crowdcoursing/api/graphql";
import {
  CalendarToday as CalendarIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Delete as DeleteIcon,
  HowToReg as HowToRegIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";

// User-facing strings
const STRINGS = {
  APPROVE: "Approve",
  ATTENDEES: "Attendees",
  DELETE_TOOLTIP: "Delete meeting time",
  PART_OF_SERIES: "Part of series:",
  PEOPLE: "people",
  PERSON: "person",
  REGISTERED_TO_ATTEND: "registered to attend. All RSVPs will be cancelled.",
  RSVP_TO_EVENT: "RSVP to this event",
  SEATS_AVAILABLE: "Seats Available",
  SELECTED_EVENT_TIME: "Selected Event Time",
  THIS_MEETING_TIME_HAS: "This meeting time has",
  UNKNOWN_USER: "Unknown User",
  UNLIMITED: "Unlimited",
  VETTED: "Vetted",
  YOURE_ATTENDING: "You're Attending",
} as const;

import { ConfirmDeleteDialog } from "~ui/molecules/ConfirmDeleteDialog/ConfirmDeleteDialog";
import { pendingApprovalBackgroundSx } from "~ui/theme/pendingApproval.theme";
import { makeProfileAvatarUrl } from "~utils/url";

type EventFromQuery = NonNullable<SessionSummaryQuery["session"]>;

interface RSVPManagementProps {
  event: EventFromQuery;
  onRSVP?: (sessionId: string) => void;
  onCancelRSVP?: (sessionId: string) => void;
  onApprovePending?: (meetingTimeId: string) => void | Promise<void>;
  isApprovingPending?: boolean;
  onDeleteMeetingTime?: (meetingTimeId: string) => void;
  isUserAttending?: boolean;
  attendees: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    isVetted: boolean;
  }[];
  selectedMeetingTime: {
    id: string;
    beginMeetingTime: string;
    endMeetingTime: string;
    capacity: number;
    series?: {
      id: string;
      name: string;
      description: string;
    };
    isPendingApproval?: boolean;
  };
}

export const RSVPManagement = ({
  event,
  onRSVP,
  onCancelRSVP,
  onApprovePending,
  isApprovingPending = false,
  onDeleteMeetingTime,
  isUserAttending,
  attendees = [],
  selectedMeetingTime,
}: RSVPManagementProps) => {
  const { beginMeetingTime, endMeetingTime, capacity, isPendingApproval } = selectedMeetingTime;
  const seatsAvailable = capacity === 0 ? STRINGS.UNLIMITED : capacity - attendees.length;
  const progressValue = capacity === 0 ? 0 : (attendees.length / capacity) * 100;

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const getInitials = (attendee: { firstName: string | null; lastName: string | null; email: string | null }) => {
    const displayName =
      attendee.firstName && attendee.lastName
        ? `${attendee.firstName} ${attendee.lastName}`
        : attendee.firstName || attendee.lastName || attendee.email || STRINGS.UNKNOWN_USER;

    if (attendee.firstName && attendee.lastName) {
      return `${attendee.firstName[0]}${attendee.lastName[0]}`.toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  };

  const handleDeleteClick = () => {
    if (attendees.length > 0) {
      setIsDeleteDialogOpen(true);
    } else {
      onDeleteMeetingTime?.(selectedMeetingTime.id);
    }
  };

  const handleDeleteConfirm = () => {
    onDeleteMeetingTime?.(selectedMeetingTime.id);
    setIsDeleteDialogOpen(false);
  };

  return (
    <Card data-testid="rsvp-management" sx={isPendingApproval ? pendingApprovalBackgroundSx : undefined}>
      <CardHeader
        title={STRINGS.SELECTED_EVENT_TIME}
        action={
          event.userIsFacilitator &&
          onDeleteMeetingTime && (
            <Tooltip title={STRINGS.DELETE_TOOLTIP}>
              <IconButton onClick={handleDeleteClick} color="error" size="small" sx={{ ml: 1 }}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )
        }
      />
      <CardContent>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CalendarIcon color="action" />
              <Typography>
                {DateTime.fromISO(beginMeetingTime).toLocaleString(DateTime.DATETIME_FULL)} -{" "}
                {DateTime.fromISO(endMeetingTime).toLocaleString(DateTime.TIME_SIMPLE)}
              </Typography>
            </Stack>
            {selectedMeetingTime.series && (
              <Typography variant="body2" color="text.secondary">
                {STRINGS.PART_OF_SERIES} {selectedMeetingTime.series.name}
              </Typography>
            )}
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {STRINGS.SEATS_AVAILABLE}: {seatsAvailable}
            </Typography>
            {capacity > 0 && <LinearProgress variant="determinate" value={progressValue} />}
          </Stack>

          {isPendingApproval && onApprovePending ? (
            <Button
              color="success"
              data-testid="approve-pending-button"
              disabled={isApprovingPending}
              fullWidth
              onClick={() => void onApprovePending(selectedMeetingTime.id)}
              startIcon={
                isApprovingPending ? <CircularProgress color="inherit" size={16} /> : <CheckCircleOutlineIcon />
              }
              variant="contained"
            >
              {STRINGS.APPROVE}
            </Button>
          ) : (
            onRSVP &&
            onCancelRSVP && (
              <Button
                variant={isUserAttending ? "outlined" : "contained"}
                onClick={() =>
                  isUserAttending ? onCancelRSVP(selectedMeetingTime.id) : onRSVP(selectedMeetingTime.id)
                }
                fullWidth
                data-testid={isUserAttending ? "cancel-rsvp-button" : "rsvp-button"}
              >
                {isUserAttending ? STRINGS.YOURE_ATTENDING : STRINGS.RSVP_TO_EVENT}
              </Button>
            )
          )}

          <Divider />

          <Typography variant="subtitle2" color="text.secondary">
            {STRINGS.ATTENDEES} ({attendees.length})
          </Typography>

          <List disablePadding data-testid="attendee-list">
            {attendees.map((attendee) => (
              <ListItem key={attendee.id} disableGutters data-testid="attendee-item">
                <ListItemAvatar>
                  <Avatar
                    src={makeProfileAvatarUrl(attendee.id)}
                    sx={{
                      backgroundColor: "primary.light",
                      color: "white",
                    }}
                  >
                    {getInitials(attendee)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${attendee.firstName} ${attendee.lastName}`}
                  secondary={attendee.email}
                  sx={{ ml: 1 }}
                  slotProps={{
                    primary: {
                      variant: "body2",
                    },

                    secondary: {
                      variant: "caption",
                    },
                  }}
                />
                {attendee.isVetted && (
                  <Tooltip title={STRINGS.VETTED}>
                    <HowToRegIcon color="success" fontSize="small" />
                  </Tooltip>
                )}
              </ListItem>
            ))}
          </List>
        </Stack>
      </CardContent>
      <ConfirmDeleteDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemType="meeting time"
        itemName={DateTime.fromISO(beginMeetingTime).toLocaleString(DateTime.DATETIME_FULL)}
        itemDescription={`${STRINGS.THIS_MEETING_TIME_HAS} ${attendees.length} ${
          attendees.length === 1 ? STRINGS.PERSON : STRINGS.PEOPLE
        } ${STRINGS.REGISTERED_TO_ATTEND}`}
      />
    </Card>
  );
};
